import type { ServiceItem } from '../../types';
import type { PlotDokument } from '../../services/plotter/plotTypen';
import { plotDateinameVorank, plotTitelVorank } from '../../services/plotter/plotStatus';
import { selectBillableServices } from '../../services/billingSelection';
import { mitPositionsauswahl, type AbschlussFall, type AbschlussSperre } from '../bestandteile/abschlussLogic';
import {
    hatOffenePlotAbrechnung,
    type OffenePlotAbrechnung,
    type VorankPlotStand,
} from '../sige-plan/plotAbrechnungen';

/**
 * Die Vorankündigung über den Plotter — reine Regeln für die Ansicht
 * (`VorankuendigungView`), damit sie dünn bleibt und das hier prüfbar ist.
 *
 * Gedruckt wird ein Aushang: die A4-Seiten gedreht nebeneinander auf der
 * Standardrolle der Firma (das Blatt baut `services/pdf/vorankPlotBlatt.ts`).
 * Abgerechnet wird wie beim SiGe-Plan erst, wenn der Auftrag beim Server
 * angelegt ist (`plotAbrechnungen.ts`), und zwar im selben Fluss wie der
 * Export — nur ohne Download, und archiviert/abgelegt wird der Stand beim
 * Klick auf „An Plotter senden“, nicht der Editorstand beim Ankommen.
 */

/** Was hinausging: der Export (Download) oder ein schon gesendeter Plot. */
export type VorankAusgabeArt = 'export' | 'plot';

/**
 * Wofür die Positionsauswahl gerade offen ist (wie `AbrechnungsZiel` im
 * SiGe-Plan-Editor). Gesetzt, BEVOR der Dialog aufgeht: die Bestätigung
 * entscheidet danach, ob heruntergeladen oder nur der Plot gebucht wird.
 */
export type VorankAbrechnungsZiel =
    | { art: 'export' }
    | { art: 'plot'; stand: VorankPlotStand };

type Aktion = 'created' | 'revised';

/**
 * Die Projekt-Id, wenn sie belastbar ist — sonst `null`. 'temp-id' ist das
 * Kennzeichen eines noch nie gespeicherten Projekts: darunter lässt sich ein
 * Plot weder in der Druckliste zuordnen noch abrechnen.
 */
export const belastbareProjektId = (id: string | null | undefined): string | null => {
    const t = (id || '').trim();
    return t && t !== 'temp-id' ? t : null;
};

const aktionWort = (aktion: Aktion): string => (aktion === 'created' ? 'Erstellung' : 'Überarbeitung');

/**
 * Buchungstext im Leistungsprotokoll. Der Export behält seinen Wortlaut
 * bis aufs Zeichen — Auswertungen und Nachweise lesen ihn.
 */
export const vorankProtokollText = (art: VorankAusgabeArt, aktion: Aktion, menge: number, einheit: string): string =>
    `Vorankündigung ${art === 'plot' ? 'geplottet' : 'exportiert'} (${aktionWort(aktion)}) (${menge} ${einheit})`;

/** Beschriftung des Archiv-Schnappschusses — sie wird auch Bezeichnung der Fassung in „Dokumente“. */
export const vorankArchivBezeichnung = (art: VorankAusgabeArt, aktion: Aktion): string =>
    `${art === 'plot' ? 'Plot' : 'PDF-Export'} ${aktionWort(aktion)}`;

/** Titel der Positionsauswahl. */
export const vorankAbrechnungsTitel = (art: VorankAusgabeArt): string =>
    art === 'plot' ? 'Geplottete Vorankündigung abrechnen' : 'Vorankündigung abrechnen';

/** Überschrift des Abschlussdialogs. */
export const vorankAbschlussTitel = (art: VorankAusgabeArt): string =>
    art === 'plot' ? 'Vorankündigung geplottet' : 'PDF exportiert';

/** Der erste Satz des Abschlussdialogs. */
export const vorankAbschlussSatz = (art: VorankAusgabeArt, gebucht: boolean): string =>
    (art === 'plot'
        ? 'Die Vorankündigung ging an den Plotter und ist als Fassung im Archiv festgehalten'
        : 'Die Vorankündigung wurde heruntergeladen und als Fassung im Archiv festgehalten')
    + (gebucht ? '; die Leistung ist gebucht.' : '.');

/**
 * Zusatz zum Fehler der Ablage: beim Versand ist die Ausgabe selbst trotzdem
 * gültig — beim Plot ist das der Druckauftrag, kein Download. Ein Entwurf ist
 * keine Ausgabe, dazu gibt es nichts zu sagen.
 */
export const vorankAblageFehlerZusatz = (anlass: 'versand' | 'entwurf', art: VorankAusgabeArt): string => {
    if (anlass !== 'versand') return '';
    return art === 'plot'
        ? ' Der Plot selbst (Druckauftrag, Buchung, Archiv) ist gültig.'
        : ' Der Export selbst (Download, Buchung, Archiv) ist gültig.';
};

/**
 * Was der Plotter-Dialog druckt. `schluessel` bündelt Sendeabsichten und
 * Auftragsliste; die Liste zeigt die Aufträge des Projekts ohne SiGe-Plan —
 * also die Vorankündigungen, nicht die Pläne.
 */
export const vorankPlotDokument = (a: {
    projectId: string;
    projektNummer: string | null | undefined;
    projektName: string | null | undefined;
}): PlotDokument => ({
    schluessel: `vorank:${a.projectId}`,
    projectId: a.projectId,
    sigeProjektId: null,
    titel: plotTitelVorank(a.projektNummer ?? '', a.projektName ?? ''),
    dateiname: plotDateinameVorank(a.projektNummer ?? ''),
    liste: { projectId: a.projectId, ohneSigePlan: true },
});

/** Tooltip des Knopfs, wenn er frei ist. */
export const VORANK_PLOT_KNOPF_TITEL =
    'Die Vorankündigung als Aushang an den Plotter senden — Seiten nebeneinander auf der Standardrolle der Firma';

/**
 * Warum „An Plotter senden“ gerade gesperrt ist — oder `null`. Die
 * Nur-Ansicht steht nicht hier: dort gibt es den Knopf gar nicht (gebucht
 * und abgelegt würde nichts).
 */
export const vorankPlotSperre = (a: { projectId: string | null | undefined; baut: boolean }): string | null => {
    if (!belastbareProjektId(a.projectId)) {
        return 'Das Projekt ist noch nicht gespeichert — ohne Projekt lässt sich der Plot weder zuordnen noch abrechnen.';
    }
    if (a.baut) return 'Das Plot-Blatt wird erzeugt …';
    return null;
};

/** Der Satz, wenn das Plot-Blatt nicht entstand (A4-PDF oder Aushang). */
export const vorankPlotBauFehlerText = (detail: string): string =>
    `Das Plot-Blatt ließ sich nicht erzeugen: ${detail}`;

/** Der Satz, wenn die Abrechnung eines angekommenen Plots scheiterte. */
export const vorankPlotAbrechnungFehlerText = (detail: string): string =>
    `Der Plot ist gesendet, seine Abrechnung aber gescheitert (${detail}). Bitte im Projekt prüfen und bei Bedarf nachbuchen.`;

/**
 * Positionsauswahl beim Plot geschlossen, ohne zu bestätigen. Bewusst wird
 * dann NICHTS gebucht, archiviert oder abgelegt (wie „Nicht ablegen“ im
 * SiGe-Plan): die Ablage als versendete Fassung erledigt per Trigger den
 * Bestandteil und kann eine Paketposition buchen — das hat der Anwender mit
 * dem Schließen gerade abgelehnt. Der Satz sagt es, damit es nicht still
 * passiert; den Druck selbst hält die Druckliste fest.
 */
export const PLOT_NICHT_ABGERECHNET_TEXT =
    'Die geplottete Vorankündigung wurde nicht abgerechnet: nichts gebucht, keine Fassung im Archiv oder in Dokumente. '
    + 'Der Druckauftrag selbst bleibt bestehen und zählt in der Druckliste.';

/**
 * Startfrage „Vorankündigung öffnen“ beim Öffnen der Ansicht? Ja, sobald auf
 * eine Vorankündigungs-Position schon gebucht ist (die Vorankündigung wurde
 * also schon erstellt).
 *
 * Die Frage gilt der GANZEN Auswahl, nie einer herausgegriffenen Zeile:
 * `[0]` hing an der Reihenfolge der Leistungsliste, und die kommt
 * ungeordnet aus der Datenbank — bei zwei Positionen („erstellen" und
 * „Aktualisierung") war es Zufall, welche gelesen wurde.
 * Positionsnummern spielen bewusst keine Rolle (siehe selectBillableServices).
 * Leere Auswahl → `some` ist false → keine Frage.
 *
 * Hier und nicht in einem Mount-Effekt: die Ansicht setzt damit den
 * ANFANGSWERT. Ein Effekt setzte die Frage erst nach dem ersten Commit —
 * im selben Commit holte der Plot-Effekt schon einen angekommenen Plot ab
 * (`plotAbrechnungAbholbar` sah die Frage noch als zu), und Startfrage und
 * Positionsauswahl bzw. Abschlussdialog standen übereinander.
 */
export const vorankStartfrageNoetig = (services: readonly ServiceItem[] | null | undefined): boolean =>
    selectBillableServices([...(services || [])], 'vorank').some(s => (s.quantityUsed ?? 0) > 0);

/**
 * Ist gerade ein Abrechnungs- oder Abschlussschritt offen? Dann wartet ein
 * angekommener Plot — sonst setzte er mitten in einer Export-Abrechnung
 * deren Ziel auf „plot“ (Codex 22.10.2026, SiGe-Plan-Editor). Dazu zählen
 * auch eine noch laufende Ablage und ein laufender Abschlussschritt: zwei
 * Ablagen nebeneinander überschrieben einander die Meldung, und zwei
 * Paketbuchungen liefen gegeneinander.
 */
export const vorankAbrechnungFensterOffen = (a: {
    /** Positionsauswahl (BillingSelectionModal). */
    positionsauswahl: boolean;
    /** Frage „Als Fassung ausgeben?“ beim geplotteten Paket-Bestandteil. */
    fassungFrage: boolean;
    /** Abschlussdialog nach Export bzw. Plot. */
    abschlussdialog: boolean;
    /** Stunden-Pflichtfenster (`stundenFensterOffen`). */
    stundenPflicht: boolean;
    /** Startfrage „Vorankündigung öffnen“ — erst beantworten, dann abrechnen. */
    startfrage: boolean;
    ablageLaeuft: boolean;
    abschlussLaeuft: boolean;
    /**
     * Der Export bereitet PDF, Fassung und Begleitblatt vor (bis zu 8 s,
     * Plan Vorankündigung unterschreiben §5) — die Positionsauswahl ist dann
     * schon zu, gebucht ist aber noch nichts.
     */
    exportLaeuft: boolean;
}): boolean =>
    a.positionsauswahl || a.fassungFrage || a.abschlussdialog || a.stundenPflicht || a.startfrage
    || a.ablageLaeuft || a.abschlussLaeuft || a.exportLaeuft;

/** Der erste Schritt einer Abrechnung (Export oder angekommener Plot). */
export type VorankAbrechnungsSchritt = 'positionsauswahl' | 'fassung_fragen' | 'direkt';

/**
 * Womit `starteAbrechnung` beginnt (Plan B5, wie `abrechnungsSchritt` im
 * SiGe-Plan-Editor):
 *  - Fall 1/3 (eigene Vorankündigungs-Position): die Positionsauswahl;
 *  - geplottet und Fall 2 (nur ein Paket mit dem Bestandteil): die Frage
 *    „Als Fassung ausgeben?“. Die Ablage als versendete Fassung erledigt per
 *    Trigger den Bestandteil und kann das Paket buchen — beim Plot hat der
 *    Anwender das noch nicht ausdrücklich gewollt (der Druck allein ist kein
 *    Versand), der SiGe-Plan fragt hier genauso;
 *  - sonst direkt ohne Auswahl. Der EXPORT im Paket-Fall bleibt bewusst
 *    direkt: dort IST der Klick auf „PDF Export & Speichern“ die Ausgabe,
 *    und so lief er schon immer;
 *  - mit Sperre (Nur-Ansicht, Freelancer, kein Schreibweg) immer direkt —
 *    gebucht würde nichts, also gibt es auch nichts zu fragen.
 */
export const vorankAbrechnungsSchritt = (a: {
    ziel: VorankAusgabeArt;
    fall: AbschlussFall;
    sperre: AbschlussSperre | null;
}): VorankAbrechnungsSchritt => {
    if (a.sperre) return 'direkt';
    if (mitPositionsauswahl(a.fall)) return 'positionsauswahl';
    if (a.ziel === 'plot' && a.fall === 'paket') return 'fassung_fragen';
    return 'direkt';
};

/**
 * Nach der Positionsauswahl: hat der Anwender beim PLOT in Fall 3 (eigene
 * Position UND Paket) „Vorgang ohne Leistungsbuchung“ gewählt, kommt erst die
 * Frage „Als Fassung ausgeben?“ — wie in Fall 2 (Betreiber 23.10.2026). Ohne
 * die Frage legte der Plot sofort als Versand ab und erledigte damit den
 * Bestandteil im Paket. Mit gebuchter Position, ohne Paket, mit Sperre und
 * beim Export geht es direkt weiter; der Export bleibt Versand wie immer.
 */
export const vorankNachPositionsauswahl = (a: {
    ziel: VorankAusgabeArt;
    fall: AbschlussFall;
    /** Die gewählte Position; `null` = „Vorgang ohne Leistungsbuchung“. */
    gebuchtePosition: string | null;
    sperre: AbschlussSperre | null;
}): 'fassung_fragen' | 'ausgeben' =>
    a.ziel === 'plot' && a.fall === 'beides' && !a.gebuchtePosition && !a.sperre ? 'fassung_fragen' : 'ausgeben';

/** Überschrift der Frage „Als Fassung ausgeben?“. */
export const VORANK_FASSUNG_FRAGE_TITEL = 'Vorankündigung als Fassung ausgeben?';

/**
 * Der Satz der Frage: was „Als Fassung ausgeben“ bewirkt, mit den Paketen,
 * deren Bestandteil die Vorankündigung ist.
 */
export const vorankFassungFrageSatz = (paketTitel: readonly string[]): string => {
    const pakete = paketTitel.map(t => `„${(t || '').trim()}“`).join(', ');
    return 'Die Vorankündigung wurde geplottet. Sie ist Bestandteil der Position '
        + `${pakete}. Als Fassung wird sie im Archiv festgehalten und als versendet in „Dokumente“ abgelegt — `
        + 'das erledigt diesen Bestandteil im Paket. Eine eigene Position wird nicht gebucht.';
};

/** Der Zusatz zu „Nicht ablegen“ in der Frage. */
export const VORANK_NICHT_ABLEGEN_HINWEIS =
    '„Nicht ablegen“: der Plot bleibt ein Zwischenstand, der Bestandteil bleibt offen. '
    + 'Der Druckauftrag zählt trotzdem in der Druckliste.';

/**
 * Das Projekt, für das JETZT eine Plot-Abrechnung abgeholt werden darf —
 * oder `null`. Nicht in der Nur-Ansicht (gebucht würde nichts, der Auftrag
 * bliebe verbraucht), nicht ohne belastbares Projekt, nicht solange ein
 * anderer Schritt offen ist. Liegen lassen heißt: nicht verbrauchen — der
 * Auftrag wartet bis zum nächsten passenden Moment.
 */
export const plotAbrechnungAbholbar = (a: {
    offen: readonly OffenePlotAbrechnung[];
    projektId: string | null | undefined;
    isReadOnly: boolean;
    fensterOffen: boolean;
}): string | null => {
    const pid = belastbareProjektId(a.projektId);
    if (!pid || a.isReadOnly || a.fensterOffen) return null;
    return hatOffenePlotAbrechnung(a.offen, pid, 'vorank') ? pid : null;
};
